// WebAuthn / Passkeys browser-side helpers

function b64urlToBuffer(b64url) {
  const b64 = (b64url || '').replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const binary = atob(b64 + pad);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
  return buf.buffer;
}

function bufferToB64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function isPasskeySupported() {
  return !!(window.PublicKeyCredential && navigator.credentials && navigator.credentials.create);
}

// Decode registration options for browser (challenge + user.id + excludeCredentials need ArrayBuffers)
function decodeRegOptions(opts) {
  return {
    ...opts,
    challenge: b64urlToBuffer(opts.challenge),
    user: { ...opts.user, id: b64urlToBuffer(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c,
      id: b64urlToBuffer(c.id),
    })),
  };
}

// Decode authentication options for browser
function decodeAuthOptions(opts) {
  return {
    ...opts,
    challenge: b64urlToBuffer(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c,
      id: b64urlToBuffer(c.id),
    })),
  };
}

// Encode registration credential for server
function encodeRegCredential(cred) {
  const res = cred.response;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(res.clientDataJSON),
      attestationObject: bufferToB64url(res.attestationObject),
    },
  };
}

// Encode authentication assertion for server
function encodeAuthAssertion(assertion) {
  const res = assertion.response;
  return {
    id: assertion.id,
    rawId: bufferToB64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufferToB64url(res.clientDataJSON),
      authenticatorData: bufferToB64url(res.authenticatorData),
      signature: bufferToB64url(res.signature),
      userHandle: res.userHandle ? bufferToB64url(res.userHandle) : null,
    },
  };
}

export async function startPasskeyRegistration(api, deviceName) {
  // 1. Get options from server
  const { data: opts } = await api.post('/auth/passkey/register-begin');

  // 2. Ask browser / authenticator to create credential
  const cred = await navigator.credentials.create({ publicKey: decodeRegOptions(opts) });
  if (!cred) throw new Error('No se creó la llave');

  // 3. Send back to server for verification
  const body = { ...encodeRegCredential(cred), name: deviceName || 'Mi dispositivo' };
  await api.post('/auth/passkey/register-complete', body);
}

export async function startPasskeyLogin(api) {
  // 1. Get challenge from server
  const { data: opts } = await api.post('/auth/passkey/auth-begin');
  const sessionKey = opts._session_key;

  // 2. Ask browser / authenticator for assertion
  const assertion = await navigator.credentials.get({ publicKey: decodeAuthOptions(opts) });
  if (!assertion) throw new Error('No se obtuvo la llave');

  // 3. Verify with server and receive JWT
  const body = { ...encodeAuthAssertion(assertion), _session_key: sessionKey };
  const { data } = await api.post('/auth/passkey/auth-complete', body);
  return data; // { token, user }
}
